import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Building2, ArrowRight, Loader2 } from 'lucide-react';
import { apiClient } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';

/**
 * Create a new team org. After creation, navigates straight to the new
 * org's dashboard. The personal-org concept stays — this only spawns
 * team orgs.
 */
export function OrgNewPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');

  const createOrg = useMutation({
    mutationFn: () => apiClient.createOrg({ name: name.trim(), domain: domain.trim() || undefined }),
    onSuccess: (res) => {
      const data = res.data as { orgId: string } | undefined;
      if (data?.orgId) {
        try { localStorage.setItem('lastOrgId', data.orgId); } catch { /* ignore */ }
        queryClient.invalidateQueries({ queryKey: ['me', 'orgs'] });
        navigate(`/orgs/${data.orgId}/dashboard`);
      }
    },
  });

  return (
    <div className="max-w-xl mx-auto pt-12 px-5 pb-12">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-10 w-10 rounded-xl bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center">
          <Building2 className="h-5 w-5 text-blue-600 dark:text-blue-400" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-foreground">Create team org</h1>
          <p className="text-sm text-muted-foreground">A shared workspace for your team's projects, members, and integrations.</p>
        </div>
      </div>

      <Card className="p-6 space-y-4">
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1 block">
            Org name
          </label>
          <Input
            placeholder="Acme Inc."
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1 block">
            Email domain (optional)
          </label>
          <Input
            placeholder="acme.com"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
          />
          <p className="text-xs text-muted-foreground mt-1.5">
            Used later for domain capture (auto-join teammates with matching email domains).
          </p>
        </div>
        <Button
          disabled={!name.trim() || createOrg.isPending}
          onClick={() => createOrg.mutate()}
          className="w-full"
        >
          {createOrg.isPending ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <ArrowRight className="h-4 w-4 mr-2" />
          )}
          Create org
        </Button>
        {createOrg.isError && (
          <p className="text-xs text-red-600">{(createOrg.error as Error).message}</p>
        )}
      </Card>
    </div>
  );
}
